import React, { useMemo, useState, useCallback } from "react";
import * as Icons from "lucide-react";
import { Search, Check } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "./ui/popover";
import { Input } from "./ui/input";
import { cn } from "../lib/utils";

// Curated shortlist first — most useful admin/dashboard icons — then all Lucide icons
// as a searchable fallback. Curated list is deduplicated against the full set at build time.
const CURATED = [
  "Activity", "ShieldAlert", "ShieldCheck", "Shield", "Lock", "Key", "KeyRound",
  "LayoutGrid", "LayoutDashboard", "MonitorCog", "MonitorCheck", "Cpu", "Server",
  "Cloud", "CloudCog", "Database", "Network", "Radar", "Radio",
  "Bell", "AlertTriangle", "AlertOctagon", "AlertCircle", "Bug",
  "BarChart2", "BarChart3", "LineChart", "PieChart", "TrendingUp", "TrendingDown",
  "FileText", "FileCheck", "FileWarning", "FileLock", "Folder", "FolderLock",
  "Users", "User", "UserCheck", "UserCog", "UserPlus", "Users2",
  "Mail", "MailCheck", "MailX", "MessageSquare", "Send",
  "Zap", "Sparkles", "Star", "Award", "Trophy", "Rocket",
  "Settings", "Sliders", "SlidersHorizontal", "Wrench", "Cog",
  "Search", "Filter", "Eye", "EyeOff",
  "Globe", "Compass", "MapPin", "Building2", "Home",
  "Terminal", "Code", "Bug", "GitBranch", "GitPullRequest",
  "Package", "Boxes", "Box",
  "Wifi", "WifiOff", "Signal", "Antenna",
  "Clock", "Calendar", "Timer",
  "Palette", "Sun", "Moon", "Monitor",
  "Trash2", "Save", "Pencil", "Plus", "X", "Check",
  "ExternalLink", "Link", "Share2", "Download", "Upload",
  "Heart", "HeartPulse", "Fingerprint", "Scan", "ScanEye", "ScanFace",
  "Play", "Pause", "SkipForward", "RotateCw",
];

// Full sorted list of all Lucide icons that are valid React components in the current build.
const ALL_ICONS = Object.keys(Icons)
  .filter((n) => /^[A-Z]/.test(n) && n !== "createLucideIcon" && n !== "default" && typeof Icons[n] === "object" && (Icons[n].$$typeof || Icons[n].render))
  .sort();

/**
 * Visual Lucide icon picker.
 *
 * value: string (Lucide component name, e.g. "ShieldAlert").
 * onChange(name): fired when user selects a new icon.
 */
export default function IconPicker({ value, onChange, size = 40, className }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);

  const Preview = Icons[value] || Icons.LayoutGrid;

  const list = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const curatedSet = new Set(CURATED);
    if (!needle) {
      // Curated first, then everything else — de-duplicated
      const rest = ALL_ICONS.filter((n) => !curatedSet.has(n));
      return [...CURATED.filter((n) => n in Icons), ...rest].slice(0, 400);
    }
    return ALL_ICONS.filter((n) => n.toLowerCase().includes(needle)).slice(0, 400);
  }, [q]);

  const pick = useCallback((name) => {
    onChange?.(name);
    setOpen(false);
  }, [onChange]);

  return (
    <div className={cn("flex items-center gap-2", className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            data-testid="icon-picker-trigger"
            className="flex items-center gap-2 rounded-lg border border-border bg-background px-2.5 py-1.5 hover:border-primary/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            style={{ minWidth: size + 24 }}
          >
            <span
              className="rounded-md bg-primary/10 border border-primary/20 flex items-center justify-center"
              style={{ width: size, height: size }}
            >
              <Preview className="text-primary" style={{ width: size * 0.55, height: size * 0.55 }} />
            </span>
            <span className="text-[12.5px] text-muted-foreground truncate font-mono">{value || "LayoutGrid"}</span>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-[380px] p-0" align="start">
          <div className="p-2 border-b border-border">
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                autoFocus
                placeholder="Search Lucide icons…"
                className="h-9 pl-8 text-[13px]"
                data-testid="icon-picker-search"
              />
            </div>
          </div>
          <div className="max-h-[320px] overflow-y-auto p-2">
            {list.length === 0 ? (
              <div className="p-6 text-center text-[12.5px] text-muted-foreground">
                No icons match &ldquo;{q}&rdquo;.
              </div>
            ) : (
              <div className="grid grid-cols-8 gap-1">
                {list.map((name) => {
                  const IconComp = Icons[name];
                  const isSel = name === value;
                  return (
                    <button
                      key={name}
                      type="button"
                      onClick={() => pick(name)}
                      title={name}
                      data-testid={`icon-picker-item-${name}`}
                      className={cn(
                        "aspect-square rounded-md border flex items-center justify-center relative hover:bg-primary/10 hover:border-primary/40 transition-colors",
                        isSel ? "border-primary bg-primary/10" : "border-transparent"
                      )}
                    >
                      <IconComp className="w-4 h-4 text-foreground" />
                      {isSel && <Check className="absolute -top-0.5 -right-0.5 w-3 h-3 text-primary" />}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <div className="border-t border-border p-2 text-[10.5px] text-muted-foreground flex items-center justify-between">
            <span>Showing {list.length} of {ALL_ICONS.length} Lucide icons</span>
            <a href="https://lucide.dev/icons" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Browse all →</a>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
